import MuleBadge from '../../components/MuleBadge';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  QuestionMarkCircleIcon,
  MapPinIcon,
  MapIcon,
  ArrowTopRightOnSquareIcon,
  ChevronUpDownIcon,
} from '@heroicons/react/24/outline';
import { Users } from 'lucide-react';
import { useMsal } from '@azure/msal-react';
import { Tooltip } from '../../components/ui/Tooltip';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import {
  useAdditionalFieldSelection,
  useAdditionalFieldColumns,
} from '../../hooks/useAdditionalFields';

/** This board's own column ids -> the registry fields they already render. Module-level
 *  for a stable identity across renders. Naming the column (rather than just the
 *  fieldname) is what lets it take the registry's title. */
const AT_RISK_COVERED_FIELDS = {
  customer_name: ['customername'],
  // The cell shows the dispatcher's name, and the username is the join key behind it,
  // so both halves are already on screen. Two fieldnames name no single title, so this
  // column keeps 'Dispatcher' unless a def called `dispatcher` matches its id.
  dispatcher: ['dispatchername', 'dispatcherusername'],
} as const;
import { TroubleTicketBadge, type TroubleTicketEntry } from '../../components/TroubleTicketBadge';
import { ServiceFailureBadge } from '../../components/ServiceFailureBadge';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import { OrderDispatcherFilter, DriverDispatcherFilter } from '../../components/filters';
import StatsGrid, { StatItem } from '../../components/StatsGrid';
import AtRiskMapDrawer from '../../components/AtRiskMapDrawer';
import DriverLoadsDrawerV2 from '../../components/DriverLoadsDrawerV2';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import GridLabel from '../../components/GridLabel';
import RequirementIcons from '../../components/RequirementIcons';
import FilterBar from '../../components/FilterBar';
import { ViewedProsProvider, useViewedPros } from '../../contexts/ViewedProsContext';
import { useDisplayPreferences } from '../../contexts/DisplayPreferencesContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { useGlobalFilters } from '../../contexts/GlobalFiltersContext';
import { useDispatchFooter } from '../../contexts/DispatchFooterContext';
import { useRealtimeCollaboration } from '../../hooks/useRealtimeCollaboration';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import api from '../../services/api';
import userSettingsService from '../../services/userSettings.service';
import { buildAtRiskMovesParams, type AtRiskDayScope } from '../../utils/atRiskMovesParams';
import { isRealDriver } from '../../utils/driverFilters';
import styles from './AtRiskMonitor.module.css';

/** "2026-09-09" -> "Wed, Sep 9". Built from the parts so the date is never
 *  shifted by a UTC parse; fixed locale so the banner reads the same everywhere. */
function formatScopeDate(iso: string | null): string | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

// Interface for reschedule history entry
interface RescheduleEntry {
  reason: string;
  old_date: string;
  new_date: string;
  created: string;
}

// Interface for at-risk move data
interface AtRiskMove {
  pronumber: number;
  legnum: number;
  movetype: string;
  driver: string;
  isMule?: boolean;
  dispatcher: string;
  region: string;
  reschedule_count: number;
  reschedule_history: RescheduleEntry[];
  trouble_ticket_count: number;
  trouble_tickets: TroubleTicketEntry[];
  order_lc: string;
  billto_name: string;
  customer_name: string;
  origin_name: string;
  origin_city_state: string;
  dest_name: string;
  dest_city_state: string;
  appointment_time: string;
  deadline_date: string;
  deadline_time: string;
  deadline_type: 'LFD' | 'Per Diem' | 'Cutoff' | 'Appointment';
  /** Client-only: set when this row is hidden by the active status/deadline
   *  buttons but surfaced because it matches the global search. */
  _filteredOut?: boolean;
  container: string;
  size: string;
  equiptype: string;
  legdrop: string;
  hot: string;
  hazmat: string;
  istank: string;
  overweightpermitrequired: string;
  chassistype: string;
  temperaturecontrolled: string;
  scac: string;
  status: string;
  estimated_minutes: number;
  time_remaining_minutes: number;
  risk_score: number;
  risk_level: 'critical' | 'at_risk' | 'on_track' | 'arrived' | 'unknown';
  risk_factor: 'recovery_time' | null;
  driver_lat: number | null;
  driver_lng: number | null;
  driver_heading: number | null;
  driver_speed: number | null;
  driver_location_updated: string | null;
  distance_to_dest_miles: number | null;
  has_realtime_location: boolean;
  origin_coordinates: string | null;
  dest_coordinates: string | null;
  origin_geofence_radius: number;
  dest_geofence_radius: number;
  per_diem_past_due: boolean;
  deadline_sort_timestamp: string | null;
  dropreleasecomment: string;
  dropreleasedatetime: string | null;
  key_account?: string;
  dest_is_new_location?: string;
  orig_is_new_location?: string;
  deliveryleg?: number;
  dest_rcvhours1?: string;
  dest_rcvhours2?: string;
  dest_contact_name?: string;
  dest_contact_phone?: string;
  dest_contact_email?: string;
  orig_rcvhours1?: string;
  orig_rcvhours2?: string;
  orig_contact_name?: string;
  orig_contact_phone?: string;
  orig_contact_email?: string;
  bol?: string;
}

// Auto-refresh interval (30 seconds)
const REFRESH_INTERVAL = 30000;

// Risk level order for sorting
const RISK_ORDER: Record<string, number> = { critical: 0, at_risk: 1, on_track: 2, arrived: 3 };

function AtRiskMonitorContent() {
  // Grid debug state
  const { showGridHighlight, showGridLabels, showGridBorders } = useDisplayPreferences();
  const { permissions, loading: permissionsLoading } = usePermissions();
  const { accounts } = useMsal();
  const { isViewed, markViewed } = useViewedPros();
  const currentUser = accounts[0];

  // Real-time collaboration - shows who else is on this page
  const { activeUsers, socket } = useRealtimeCollaboration({
    pageId: 'at-risk-monitor',
    userName: currentUser?.name || currentUser?.username || 'Anonymous',
    userId: currentUser?.localAccountId || currentUser?.homeAccountId,
    userEmail: currentUser?.username,
  });

  // Footer context for showing active users
  const { setDispatchButton } = useDispatchFooter();

  // Data state - baseData for stats, filteredMoves for table display
  const [baseData, setBaseData] = useState<AtRiskMove[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Projected ETAs from driver status monitor (keyed by "pronumber-legnum")
  const [driverETAs, setDriverETAs] = useState<Record<string, string>>({});

  // Initialization state - prevents flicker when home region is applied
  const [initializing, setInitializing] = useState(true);

  // Track dark mode for tooltip content (rendered via portal to document.body —
  // inline styles avoid relying on Tailwind dark: utilities being generated)

  // Global filters (shared across pages)
  const {
    selectedRegions,
    setSelectedRegions,
    selectedOrderDispatchers,
    setSelectedOrderDispatchers,
    selectedDriverDispatchers,
    setSelectedDriverDispatchers,
    isLoading: globalFiltersLoading,
  } = useGlobalFilters();

  // Local filters (page-specific, but still persisted)
  const [selectedCSRs, setSelectedCSRs] = useState<string[]>([]);
  const [selectedSalesReps, setSelectedSalesReps] = useState<string[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedRiskLevels, setSelectedRiskLevels] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedDeadlineTypes, setSelectedDeadlineTypes] = useState<string[]>([]);
  const [selectedKeyStatuses, setSelectedKeyStatuses] = useState<string[]>([]);
  const localFiltersLoaded = useRef(false);

  // Day scope: Today (live board) or Tomorrow (only moves whose appointment or
  // deadline falls tomorrow), for night dispatchers planning past midnight.
  // Deliberately NOT persisted: every load opens on Today so day shift never
  // inherits a Tomorrow board. The night dispatcher clicks it once per shift.
  // "Tomorrow" is relative — after midnight the poll rolls forward and the
  // banner date (from the response) changes, which is the cue to flip back.
  const [dayScope, setDayScope] = useState<AtRiskDayScope>('today');
  const [scopeDate, setScopeDate] = useState<string | null>(null);
  // Session-only: include all drivers on every visit.
  const [showMules, setShowMules] = useState(true);

  // Load local filters from user settings
  useEffect(() => {
    if (localFiltersLoaded.current) return;
    const loadLocalFilters = async () => {
      try {
        const setting = await userSettingsService.getUserSetting('at_risk_monitor_filters');
        if (setting?.setting_value) {
          const filters = setting.setting_value;
          if (filters.selectedCSRs) setSelectedCSRs(filters.selectedCSRs);
          if (filters.selectedSalesReps) setSelectedSalesReps(filters.selectedSalesReps);
          if (filters.selectedCustomers) setSelectedCustomers(filters.selectedCustomers);
          if (filters.selectedRiskLevels) setSelectedRiskLevels(filters.selectedRiskLevels);
          if (filters.selectedStatuses) setSelectedStatuses(filters.selectedStatuses);
          if (filters.selectedDeadlineTypes) setSelectedDeadlineTypes(filters.selectedDeadlineTypes);
          if (filters.selectedKeyStatuses) setSelectedKeyStatuses(filters.selectedKeyStatuses);
        }
      } catch (error) {
        // Silently fail
      }
      localFiltersLoaded.current = true;
    };
    loadLocalFilters();
  }, []);

  // Auto-save local filters when they change (debounced)
  const saveLocalFiltersTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    // Don't save until initial load is complete
    if (!localFiltersLoaded.current) return;

    if (saveLocalFiltersTimeoutRef.current) clearTimeout(saveLocalFiltersTimeoutRef.current);
    saveLocalFiltersTimeoutRef.current = setTimeout(async () => {
      try {
        await userSettingsService.saveUserSetting('at_risk_monitor_filters', {
          selectedCSRs,
          selectedSalesReps,
          selectedCustomers,
          selectedRiskLevels,
          selectedStatuses,
          selectedDeadlineTypes,
          selectedKeyStatuses,
        });
      } catch (error) {
        console.error('Failed to save local filters:', error);
      }
    }, 500);

    return () => {
      if (saveLocalFiltersTimeoutRef.current) clearTimeout(saveLocalFiltersTimeoutRef.current);
    };
  }, [selectedCSRs, selectedSalesReps, selectedCustomers, selectedRiskLevels, selectedStatuses, selectedDeadlineTypes, selectedKeyStatuses]);

  // One-time migration: insert trouble_ticket_count column after reschedule_count in saved order
  useEffect(() => {
    const MIGRATION_KEY = 'at_risk_tt_col_migrated';
    if (localStorage.getItem(MIGRATION_KEY)) return;
    const migrateColumnOrder = async () => {
      try {
        const setting = await userSettingsService.getUserSetting('table_at-risk-monitor');
        if (!setting?.setting_value?.columnOrder) {
          localStorage.setItem(MIGRATION_KEY, '1');
          return;
        }
        const order: string[] = setting.setting_value.columnOrder;
        if (order.includes('trouble_ticket_count')) {
          localStorage.setItem(MIGRATION_KEY, '1');
          return;
        }
        const rsIdx = order.indexOf('reschedule_count');
        if (rsIdx !== -1) {
          order.splice(rsIdx + 1, 0, 'trouble_ticket_count');
        } else {
          order.push('trouble_ticket_count');
        }
        await userSettingsService.saveUserSetting('table_at-risk-monitor', {
          ...setting.setting_value,
          columnOrder: order,
        });
        localStorage.setItem(MIGRATION_KEY, '1');
      } catch {
        // Silently fail — column will just appear at end
      }
    };
    migrateColumnOrder();
  }, []);

  // Map drawer state
  const [mapDrawerMove, setMapDrawerMove] = useState<AtRiskMove | null>(null);

  // Driver loads drawer state
  const [selectedDriverUnit, setSelectedDriverUnit] = useState<string | null>(null);

  // Order details drawer state
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);
  const [selectedPro, setSelectedPro] = useState<number | null>(null);

  // Filter options
  const [regionOptions, setRegionOptions] = useState<MultiSelectOption[]>([]);
  const [csrOptions, setCSROptions] = useState<MultiSelectOption[]>([]);
  const [salesRepOptions, setSalesRepOptions] = useState<MultiSelectOption[]>([]);
  const [customerOptions, setCustomerOptions] = useState<MultiSelectOption[]>([]);
  const homeRegionInitialized = useRef(false);
  // STATE, not a ref: opening this gate must re-run the data-fetch effect even
  // when no filter changed in the same commit. With a ref, a boot where the
  // last filter write landed BEFORE initialization finished (e.g. permissions
  // resolving after saved filters, with no region to apply) issued no fetch at
  // all — the grid sat empty until the 30s poll (prod, 2026-08-25: "loads in
  // 3s or 31s, hit and miss").
  const [readyToFetch, setReadyToFetch] = useState(false);
  const initialRegionRef = useRef<string[] | null>(null);

  // Auto-select user's home region when available (only if no global region is set)
  useEffect(() => {
    if (homeRegionInitialized.current) return;
    // Wait for permissions and global filters to finish loading
    if (permissionsLoading || globalFiltersLoading) return;

    // If global region is already set, use it.
    //
    // Deliberately checked BEFORE regionOptions is required: a saved region
    // needs no validation against the options list, so this path must not wait
    // on /filter-options. It used to, which serialised the whole page — the
    // grid could not even begin fetching until a ~2s dropdown call returned.
    if (selectedRegions.length > 0) {
      initialRegionRef.current = selectedRegions;
      homeRegionInitialized.current = true;
      setReadyToFetch(true);
      setInitializing(false);
      return;
    }

    // Applying the home-region default DOES need the options list, because the
    // region is only used if it's actually offered — an unknown region would
    // filter the grid down to nothing. This path still waits.
    if (regionOptions.length === 0) return;

    // If user has a home region and no global region is set, apply it
    let regionToSet: string[] = [];
    if (permissions?.home_region && permissions?.home_region_as_default !== false) {
      if (regionOptions.some(opt => opt.value === permissions.home_region)) {
        regionToSet = [permissions.home_region];
        setSelectedRegions(regionToSet);
      }
    }
    // Store the initial region in a ref so fetchMoves can use it directly
    initialRegionRef.current = regionToSet;
    homeRegionInitialized.current = true;
    setReadyToFetch(true);
    setInitializing(false);
  }, [permissions?.home_region, permissions?.home_region_as_default, permissionsLoading, regionOptions, globalFiltersLoading, selectedRegions, setSelectedRegions]);

  // Any orders-scope registry field can be added as a column. A row here is a MOVE,
  // (pronumber, legnum), so a pro with several at-risk legs repeats the same order
  // values down each of its rows — they are sibling rows, not a parent with
  // sub-rows, so there is no row to blank the repeats in favour of.
  const fieldSelection = useAdditionalFieldSelection('at-risk-monitor');
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');

  const fetchAbortControllerRef = useRef<AbortController | null>(null);
  // Query string of the last at-risk-moves fetch, used to drop redundant
  // repeats triggered by staged filter initialisation (see fetchMoves).
  const lastFetchSignatureRef = useRef<string | null>(null);
  // Query string of the last cascade fetch, for the same reason.
  const lastCascadeSignatureRef = useRef<string | null>(null);
  // Admin kill switch for the driver-ETA fetch, delivered on the filter-options
  // response. A ref, not state, so flipping it never re-runs the fetch effect.
  const driverEtasEnabledRef = useRef(true);

  // Format minutes to human readable
  const formatMinutes = useCallback((minutes: number) => {
    const isNegative = minutes < 0;
    const absMinutes = Math.abs(minutes);
    const prefix = isNegative ? '-' : '';

    if (absMinutes >= 1440) {
      const days = Math.floor(absMinutes / 1440);
      const hours = Math.floor((absMinutes % 1440) / 60);
      const mins = absMinutes % 60;
      return `${prefix}${days}d ${hours}h ${mins}m`;
    }
    if (absMinutes >= 60) {
      const hours = Math.floor(absMinutes / 60);
      const mins = absMinutes % 60;
      return `${prefix}${hours}h ${mins}m`;
    }
    return `${prefix}${absMinutes}m`;
  }, []);

  // Get effective CSR filter values (backend will expand tilde-delimited group values)
  const getEffectiveCSRs = useCallback(() => {
    return selectedCSRs;
  }, [selectedCSRs]);

  // Fetch filter options (regions, CSRs - dispatchers handled by filter components)
  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await api.get('/at-risk-monitor/filter-options');
      const { regions, csrs, salesReps, customers, driverEtasEnabled } = response.data;

      // Admin kill switch (migration 392). Defaults to on so an older backend,
      // or a failed lookup, keeps today's behaviour rather than silently
      // dropping ETAs.
      driverEtasEnabledRef.current = driverEtasEnabled !== false;

      setRegionOptions(
        regions.map((r: string) => ({ value: r, label: r }))
      );

      // `csrs` is one merged list (LC groups + individual LCs) tagged with isGroup;
      // split it into LCs + Groups for the sectioned dropdown. Each LC covers the
      // order LC (csr) OR warehouse LC (whsecsr); group values are tilde-delimited
      // username lists that expand server-side.
      type LcOption = { value: string; label: string; isGroup?: boolean };
      const lcOptions = (csrs as LcOption[])
        .filter((c) => !c.isGroup)
        .map((c) => ({ value: c.value, label: c.label }));
      const groupOptions = (csrs as LcOption[])
        .filter((c) => c.isGroup)
        .map((g) => ({ value: g.value, label: g.label }));

      const lcsHeader = { value: '__lcs_header__', label: 'LCs', isHeader: true };
      const divider = { value: '__divider__', label: '', isDivider: true };
      const groupsHeader = { value: '__groups_header__', label: 'Groups', isHeader: true };

      setCSROptions(
        groupOptions.length > 0
          ? [lcsHeader, ...lcOptions, divider, groupsHeader, ...groupOptions]
          : lcOptions
      );

      setSalesRepOptions(
        (salesReps || []).map((s: { value: string; label: string }) => ({ value: s.value, label: s.label }))
      );

      setCustomerOptions(
        customers.map((c: { value: string; label: string }) => ({ value: c.value, label: c.label }))
      );
    } catch (err) {
      console.error('Failed to fetch filter options:', err);
    }
  }, []);

  // Cascade the dependent option lists: LC narrows Sales Rep, and LC + Sales Rep
  // narrow Customer. One-directional on purpose — a rep never narrows the LC list,
  // so the two dropdowns can't fight each other.
  const fetchCascadeOptions = useCallback(async () => {
    const params = new URLSearchParams();
    const effectiveCSRs = getEffectiveCSRs();
    if (effectiveCSRs.length > 0) {
      params.append('csr', [...effectiveCSRs].sort().join(','));
    }
    if (selectedSalesReps.length > 0) {
      params.append('salesrep', [...selectedSalesReps].sort().join(','));
    }
    const query = params.toString();

    // Same staged-initialisation problem as fetchMoves: this effect is keyed on
    // selectedCSRs AND on fetchCascadeOptions (whose identity changes with
    // selectedSalesReps), so it re-ran up to three times per load for the same
    // filters. Measured at ~1s per call, all returning identical data.
    if (query === lastCascadeSignatureRef.current) {
      return;
    }
    lastCascadeSignatureRef.current = query;

    try {
      const response = await api.get(`/at-risk-monitor/filter-options?${query}`);
      const { salesReps, customers } = response.data;

      const newSalesRepOptions = (salesReps || []).map((s: { value: string; label: string }) => ({
        value: s.value,
        label: s.label,
      }));
      setSalesRepOptions(newSalesRepOptions);

      const newCustomerOptions = customers.map((c: { value: string; label: string }) => ({
        value: c.value,
        label: c.label,
      }));
      setCustomerOptions(newCustomerOptions);

      // Drop selections that the narrowed lists no longer offer. Keep the previous
      // array identity when nothing was dropped — this runs in an effect keyed on
      // selectedSalesReps, so a fresh array every pass would loop forever.
      const validRepValues = new Set(newSalesRepOptions.map((s: MultiSelectOption) => s.value));
      setSelectedSalesReps((prev) => {
        const next = prev.filter((s) => validRepValues.has(s));
        return next.length === prev.length ? prev : next;
      });

      const validCustomerValues = new Set(newCustomerOptions.map((c: MultiSelectOption) => c.value));
      setSelectedCustomers((prev) => {
        const next = prev.filter((c) => validCustomerValues.has(c));
        return next.length === prev.length ? prev : next;
      });
    } catch (err) {
      // Allow a retry with the same filters — the signature is recorded before
      // the request, so leaving it set would strand the dropdowns on failure.
      if (lastCascadeSignatureRef.current === query) {
        lastCascadeSignatureRef.current = null;
      }
      console.error('Failed to fetch cascaded filter options:', err);
    }
  }, [getEffectiveCSRs, selectedSalesReps]);

  // Fetch at-risk moves
  // overrideRegions allows bypassing the closure when we know the region value explicitly
  const fetchMoves = useCallback(async (isBackgroundRefresh = false, overrideRegions?: string[]) => {
    // Skip a repeat of the filter set we already have.
    //
    // The page writes its filter state in stages while booting — permissions
    // apply a default region, saved global filters restore, the cascade prunes
    // reps and customers — and each write re-runs the fetch effect. Those
    // stages land seconds apart, so the abort below never catches them: the
    // page issued this same 237 kB request four times on load, each one also
    // triggering its own batch-driver-etas.
    //
    // Background refreshes bypass this — they want fresh data for unchanged
    // filters, which is the whole point of the poll.
    const signature = buildAtRiskMovesParams({
      orderDispatchers: selectedOrderDispatchers,
      driverDispatchers: selectedDriverDispatchers,
      regions: overrideRegions !== undefined ? overrideRegions : selectedRegions,
      csrs: getEffectiveCSRs(),
      salesReps: selectedSalesReps,
      customers: selectedCustomers,
      day: dayScope,
      showMules,
      additionalFields: fieldSelection.additionalFields,
    });
    if (!isBackgroundRefresh && signature === lastFetchSignatureRef.current) {
      return;
    }
    lastFetchSignatureRef.current = signature;

    // Abort any in-flight request to prevent race conditions
    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort();
    }

    // Create new controller for this request
    const controller = new AbortController();
    fetchAbortControllerRef.current = controller;

    if (!isBackgroundRefresh) {
      setLoading(true);
    }
    setError(null);

    try {
      // `signature` above is this request's query string — built once so the
      // duplicate check can never drift from what actually gets sent.
      const response = await api.get(`/at-risk-monitor/at-risk-moves?${signature}`, {
        signal: controller.signal
      });
      const data: AtRiskMove[] = response.data.data || [];
      setScopeDate(response.data.scope_date ?? null);

      // Filter out placeholder/admin drivers, but keep Not Dispatched moves (no driver assigned)
      const filteredData = data.filter((m) => m.status === 'Not Dispatched' || isRealDriver(m.driver));
      setBaseData(filteredData);

      // Projected ETAs are decoration on rows the user can already read, and
      // the call was measured at 1-6s. It used to be awaited here, which meant
      // setLoading(false) in the finally below waited on it and the whole grid
      // stayed behind a spinner. Fire and forget: the table paints now, the ETA
      // column fills in when it arrives.
      const uniqueDrivers = [...new Set(
        filteredData
          .filter(m => m.driver && m.status !== 'Not Dispatched' && m.status !== 'Completed' && m.status !== 'At Destination')
          .map(m => m.driver)
      )];
      if (!driverEtasEnabledRef.current) {
        // Switched off by an admin (at_risk.driver_etas_enabled). The page
        // renders without projected ETAs; nothing else is affected.
        setDriverETAs({});
      } else if (uniqueDrivers.length > 0) {
        // Not awaited. Carries this fetch's abort signal so a superseded load
        // can't land stale ETAs over newer rows.
        api.post('/driver-status-monitor/batch-driver-etas',
          { unit_numbers: uniqueDrivers },
          { signal: controller.signal }
        )
          .then(etaResponse => {
            if (!controller.signal.aborted) {
              setDriverETAs(etaResponse.data.etas || {});
            }
          })
          .catch(() => {
            // Non-critical - don't fail the whole page if ETAs fail
            console.error('Failed to fetch driver ETAs');
          });
      } else {
        setDriverETAs({});
      }
    } catch (err: any) {
      // Ignore abort errors - they're expected when we cancel stale requests.
      // Deliberately does NOT clear the signature: the request that superseded
      // this one has already recorded its own.
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return;
      }
      // A failed fetch must not block a retry with the same filters — the
      // signature is recorded before the request, so leaving it set would make
      // the page stick on the error until a filter changed. Only clear it if a
      // newer fetch hasn't already claimed it.
      if (lastFetchSignatureRef.current === signature) {
        lastFetchSignatureRef.current = null;
      }
      setError(err.response?.data?.detail || 'Failed to fetch at-risk moves');
    } finally {
      setLoading(false);
    }
  }, [selectedOrderDispatchers, selectedDriverDispatchers, selectedRegions, selectedCSRs, selectedSalesReps, selectedCustomers, dayScope, showMules, getEffectiveCSRs, additionalFieldsKey]);

  // Build key status options from data
  const keyStatusOptions = useMemo<MultiSelectOption[]>(() => {
    const counts = new Map<string, number>();
    for (const m of baseData) {
      const status = m.key_account?.toUpperCase();
      if (status && status !== 'AVERAGE') {
        counts.set(status, (counts.get(status) || 0) + 1);
      }
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([status, count]) => ({
        value: status,
        label: `${status.split(' ').map(w => w[0] + w.slice(1).toLowerCase()).join(' ')} (${count})`,
      }));
  }, [baseData]);

  // Filtered moves for table display (applies stat grid filters)
  const moves = useMemo(() => {
    let data = [...baseData];
    // 'unknown' rows (no deadline could be computed) always bypass the risk-level
    // and deadline-type buttons so a pro is never silently hidden just because its
    // status couldn't be determined. Dispatch-status and key-account still apply —
    // those dimensions are knowable for an unknown-deadline leg.
    if (selectedRiskLevels.length > 0) {
      data = data.filter(m => selectedRiskLevels.includes(m.risk_level) || m.risk_level === 'unknown');
    }
    if (selectedStatuses.length > 0) {
      data = data.filter(m => selectedStatuses.includes(m.status));
    }
    if (selectedDeadlineTypes.length > 0) {
      data = data.filter(m => selectedDeadlineTypes.includes(m.deadline_type) || m.risk_level === 'unknown');
    }
    if (selectedKeyStatuses.length > 0) {
      data = data.filter(m => selectedKeyStatuses.includes(m.key_account?.toUpperCase() || ''));
    }
    return data;
  }, [baseData, selectedRiskLevels, selectedStatuses, selectedDeadlineTypes, selectedKeyStatuses]);

  // When a global search is active, also surface rows the status/deadline buttons
  // hid (baseData ∖ moves). TanTable's own global filter then keeps only those
  // matching the search term; getRowClassName flags them. They collapse away as
  // soon as the search is cleared. (LC/region/customer filters are server-side, so
  // baseData already excludes those — only the top-level buttons are bypassed here.)
  const [tableSearch, setTableSearch] = useState('');
  const tableData = useMemo(() => {
    if (!tableSearch.trim()) return moves;
    const rowKey = (m: AtRiskMove) => `${m.pronumber}-${m.legnum}`;
    const shown = new Set(moves.map(rowKey));
    const filteredOut = baseData
      .filter(m => !shown.has(rowKey(m)))
      .map(m => ({ ...m, _filteredOut: true }));
    return filteredOut.length ? [...moves, ...filteredOut] : moves;
  }, [moves, baseData, tableSearch]);

  // Enhance customer options with move counts from current data
  // Customers with moves appear first (alphabetically), then a divider, then customers without moves
  const customerOptionsWithCounts = useMemo(() => {
    if (!baseData.length) return customerOptions;

    const countsByName = baseData.reduce((acc, move) => {
      if (move.customer_name) {
        acc[move.customer_name] = (acc[move.customer_name] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    const withMoves = customerOptions
      .filter(opt => countsByName[opt.label])
      .map(opt => ({
        ...opt,
        label: `${opt.label} (${countsByName[opt.label]})`
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const withoutMoves = customerOptions
      .filter(opt => !countsByName[opt.label])
      .sort((a, b) => a.label.localeCompare(b.label));

    if (withMoves.length > 0 && withoutMoves.length > 0) {
      return [
        ...withMoves,
        { value: '__divider__', label: '', isDivider: true },
        ...withoutMoves
      ];
    }

    return [...withMoves, ...withoutMoves];
  }, [customerOptions, baseData]);

  // Initial load - only fetch filter options, data fetch waits for initialization
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Re-fetch the cascaded option lists when the LC or Sales Rep filter changes
  useEffect(() => {
    fetchCascadeOptions();
  }, [selectedCSRs, fetchCascadeOptions]);

  // Refetch when filters change - only after initialization is complete.
  // readyToFetch is in the deps ON PURPOSE: the commit that opens the gate must
  // itself fire the boot fetch, because no watched filter may change after it.
  useEffect(() => {
    // The saved column selection is part of the request, so it gates the boot fetch
    // for the same reason readyToFetch does — going early just fetches twice.
    if (!readyToFetch || !fieldSelection.ready) return;

    // For the initial fetch, use the region from the ref (set before state update)
    // This bypasses the stale closure issue
    if (initialRegionRef.current !== null) {
      const initialRegion = initialRegionRef.current;
      initialRegionRef.current = null; // Clear so subsequent fetches use state
      fetchMoves(false, initialRegion);
    } else {
      fetchMoves();
    }
  }, [readyToFetch, fieldSelection.ready, selectedOrderDispatchers, selectedDriverDispatchers, selectedRegions, selectedCSRs, selectedSalesReps, selectedCustomers, dayScope, showMules, fetchMoves]);

  // Auto-refresh setup - pause when map drawer is open
  useVisibilityAwareInterval(() => {
    fetchMoves(true);
  }, mapDrawerMove !== null ? null : REFRESH_INTERVAL);

  // Update footer with active users display
  useEffect(() => {
    // Filter out current user and dedupe by user_id
    const currentUserId = activeUsers.find(user => user.sid === socket?.id)?.user_id;
    const seen = new Set<string>();
    const otherUsers = activeUsers.filter(user => {
      if (user.user_id === currentUserId || seen.has(user.user_id)) {
        return false;
      }
      seen.add(user.user_id);
      return true;
    });
    const maxVisible = 10;
    const visibleUsers = otherUsers.slice(0, maxVisible);
    const overflowUsers = otherUsers.slice(maxVisible);

    const getInitials = (name: string): string => {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    };

    const getUserColor = (name: string): string => {
      const colors = [
        'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500',
        'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500',
      ];
      const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      return colors[hash % colors.length];
    };

    setDispatchButton(
      <div className="flex items-center justify-between w-full">
        {/* Active Users Display - Left Side */}
        <div className="flex items-center gap-2">
          {otherUsers.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <Users className="h-4 w-4 text-gray-600 dark:text-gray-300" />
              <div className="flex -space-x-1">
                {visibleUsers.map(user => (
                  <Tooltip key={user.user_id} content={user.user_name}>
                    <div
                      className={`inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-white dark:border-gray-700 text-[0.625rem] font-medium text-white cursor-pointer ${getUserColor(user.user_name)}`}
                    >
                      {getInitials(user.user_name)}
                    </div>
                  </Tooltip>
                ))}
                {overflowUsers.length > 0 && (
                  <Tooltip
                    content={
                      <div className="flex flex-col gap-1">
                        {overflowUsers.map(user => (
                          <span key={user.user_id}>{user.user_name}</span>
                        ))}
                      </div>
                    }
                  >
                    <div className="inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-white dark:border-gray-700 bg-gray-500 text-[0.625rem] font-medium text-white cursor-pointer">
                      +{overflowUsers.length}
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex-1" />
      </div>
    );

    // Cleanup on unmount
    return () => {
      setDispatchButton(null);
    };
  }, [activeUsers, socket?.id, setDispatchButton]);

  // Open order details drawer
  const openOrderDrawer = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsOrderDrawerOpen(true);
  }, []);

  // Close order details drawer
  const closeOrderDrawer = useCallback(() => {
    setIsOrderDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  // Toggle helper for multi-select
  const toggleArrayValue = (arr: string[], value: string): string[] => {
    return arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
  };

  // Apply key status filter from MultiSelect (affects all stat counts)
  const keyStatusFilteredData = useMemo(() => {
    if (selectedKeyStatuses.length === 0) return baseData;
    return baseData.filter(m => selectedKeyStatuses.includes(m.key_account?.toUpperCase() || ''));
  }, [baseData, selectedKeyStatuses]);

  // Cascading filtered data for stats
  const dataForStatusStats = useMemo(() => {
    let data = [...keyStatusFilteredData];
    if (selectedRiskLevels.length > 0) {
      data = data.filter(m => selectedRiskLevels.includes(m.risk_level));
    }
    return data;
  }, [keyStatusFilteredData, selectedRiskLevels]);

  const dataForDeadlineStats = useMemo(() => {
    let data = [...keyStatusFilteredData];
    if (selectedRiskLevels.length > 0) {
      data = data.filter(m => selectedRiskLevels.includes(m.risk_level));
    }
    if (selectedStatuses.length > 0) {
      data = data.filter(m => selectedStatuses.includes(m.status));
    }
    return data;
  }, [keyStatusFilteredData, selectedRiskLevels, selectedStatuses]);

  // Build stats for StatsGrid - Risk Level
  const riskLevelStats: StatItem[] = useMemo(() => [
    {
      id: 'critical',
      label: 'Critical',
      description: 'Will be late',
      value: keyStatusFilteredData.filter(m => m.risk_level === 'critical').length,
      colorClass: 'text-red-700 dark:text-red-300',
      bgClass: 'bg-red-100 dark:bg-red-900/30',
      selectedBgClass: 'bg-red-200 dark:bg-red-900/50',
      selectedBorderColor: '#dc2626',
      selected: selectedRiskLevels.includes('critical'),
      dimmed: selectedRiskLevels.length > 0 && !selectedRiskLevels.includes('critical'),
      onClick: () => setSelectedRiskLevels(prev => toggleArrayValue(prev, 'critical')),
    },
    {
      id: 'at_risk',
      label: 'At Risk',
      description: 'Less than 30 min buffer',
      value: keyStatusFilteredData.filter(m => m.risk_level === 'at_risk').length,
      colorClass: 'text-yellow-700 dark:text-yellow-300',
      bgClass: 'bg-amber-50 dark:bg-yellow-900/20',
      selectedBgClass: 'bg-amber-100 dark:bg-yellow-900/40',
      selectedBorderColor: '#ca8a04',
      selected: selectedRiskLevels.includes('at_risk'),
      dimmed: selectedRiskLevels.length > 0 && !selectedRiskLevels.includes('at_risk'),
      onClick: () => setSelectedRiskLevels(prev => toggleArrayValue(prev, 'at_risk')),
    },
    {
      id: 'on_track',
      label: 'On Track',
      description: '30+ min buffer',
      value: keyStatusFilteredData.filter(m => m.risk_level === 'on_track').length,
      colorClass: 'text-green-700 dark:text-green-300',
      bgClass: 'bg-green-100 dark:bg-green-900/30',
      selectedBgClass: 'bg-green-200 dark:bg-green-900/50',
      selectedBorderColor: '#16a34a',
      selected: selectedRiskLevels.includes('on_track'),
      dimmed: selectedRiskLevels.length > 0 && !selectedRiskLevels.includes('on_track'),
      onClick: () => setSelectedRiskLevels(prev => toggleArrayValue(prev, 'on_track')),
    },
  ], [keyStatusFilteredData, selectedRiskLevels]);

  // Build stats for StatsGrid - Status
  const statusStats: StatItem[] = useMemo(() => [
    {
      id: 'not-dispatched',
      label: 'Not Dispatched',
      value: dataForStatusStats.filter(m => m.status === 'Not Dispatched').length,
      colorClass: 'text-orange-600 dark:text-orange-400',
      selectedBgClass: 'bg-orange-200 dark:bg-orange-900/50',
      selectedBorderColor: '#ea580c',
      selected: selectedStatuses.includes('Not Dispatched'),
      dimmed: selectedStatuses.length > 0 && !selectedStatuses.includes('Not Dispatched'),
      onClick: () => setSelectedStatuses(prev => toggleArrayValue(prev, 'Not Dispatched')),
    },
    {
      id: 'dispatched',
      label: 'Not Started',
      value: dataForStatusStats.filter(m => m.status === 'Dispatched').length,
      colorClass: 'text-body',
      selectedBgClass: 'bg-gray-200 dark:bg-gray-700',
      selectedBorderColor: '#4b5563',
      selected: selectedStatuses.includes('Dispatched'),
      dimmed: selectedStatuses.length > 0 && !selectedStatuses.includes('Dispatched'),
      onClick: () => setSelectedStatuses(prev => toggleArrayValue(prev, 'Dispatched')),
    },
    {
      id: 'at-origin',
      label: 'At Origin',
      value: dataForStatusStats.filter(m => m.status === 'At Origin').length,
      colorClass: 'text-body',
      selectedBgClass: 'bg-gray-200 dark:bg-gray-700',
      selectedBorderColor: '#4b5563',
      selected: selectedStatuses.includes('At Origin'),
      dimmed: selectedStatuses.length > 0 && !selectedStatuses.includes('At Origin'),
      onClick: () => setSelectedStatuses(prev => toggleArrayValue(prev, 'At Origin')),
    },
    {
      id: 'in-transit',
      label: 'In Transit',
      value: dataForStatusStats.filter(m => m.status === 'In Transit').length,
      colorClass: 'text-body',
      selectedBgClass: 'bg-gray-200 dark:bg-gray-700',
      selectedBorderColor: '#4b5563',
      selected: selectedStatuses.includes('In Transit'),
      dimmed: selectedStatuses.length > 0 && !selectedStatuses.includes('In Transit'),
      onClick: () => setSelectedStatuses(prev => toggleArrayValue(prev, 'In Transit')),
    },
    {
      id: 'at-destination',
      label: 'At Destination',
      value: dataForStatusStats.filter(m => m.status === 'At Destination').length,
      colorClass: 'text-body',
      selectedBgClass: 'bg-gray-200 dark:bg-gray-700',
      selectedBorderColor: '#4b5563',
      selected: selectedStatuses.includes('At Destination'),
      dimmed: selectedStatuses.length > 0 && !selectedStatuses.includes('At Destination'),
      onClick: () => setSelectedStatuses(prev => toggleArrayValue(prev, 'At Destination')),
    },
  ], [dataForStatusStats, selectedStatuses]);

  // Build stats for StatsGrid - Deadline Type (with Hyper Focus prepended)
  const deadlineTypeStats: StatItem[] = useMemo(() => [
    {
      id: 'hyper-focus',
      label: 'Hyper Focus',
      value: dataForDeadlineStats.filter(m => m.key_account?.toUpperCase() === 'HYPER FOCUS').length,
      colorClass: 'text-blue-600 dark:text-blue-400',
      selectedBgClass: 'bg-blue-100 dark:bg-blue-900/30',
      selectedBorderColor: '#3b82f6',
      selected: selectedKeyStatuses.includes('HYPER FOCUS'),
      dimmed: selectedKeyStatuses.length > 0 && !selectedKeyStatuses.includes('HYPER FOCUS'),
      onClick: () => setSelectedKeyStatuses(prev => toggleArrayValue(prev, 'HYPER FOCUS')),
    },
    {
      id: 'lfd',
      label: 'LFD',
      value: dataForDeadlineStats.filter(m => m.deadline_type === 'LFD').length,
      colorClass: 'text-body',
      selectedBgClass: 'bg-gray-200 dark:bg-gray-700',
      selectedBorderColor: '#4b5563',
      selected: selectedDeadlineTypes.includes('LFD'),
      dimmed: selectedDeadlineTypes.length > 0 && !selectedDeadlineTypes.includes('LFD'),
      onClick: () => setSelectedDeadlineTypes(prev => toggleArrayValue(prev, 'LFD')),
    },
    {
      id: 'per-diem',
      label: 'Per Diem',
      value: dataForDeadlineStats.filter(m => m.deadline_type === 'Per Diem').length,
      colorClass: 'text-body',
      selectedBgClass: 'bg-gray-200 dark:bg-gray-700',
      selectedBorderColor: '#4b5563',
      selected: selectedDeadlineTypes.includes('Per Diem'),
      dimmed: selectedDeadlineTypes.length > 0 && !selectedDeadlineTypes.includes('Per Diem'),
      onClick: () => setSelectedDeadlineTypes(prev => toggleArrayValue(prev, 'Per Diem')),
    },
    {
      id: 'cutoff',
      label: 'Cutoff',
      value: dataForDeadlineStats.filter(m => m.deadline_type === 'Cutoff').length,
      colorClass: 'text-body',
      selectedBgClass: 'bg-gray-200 dark:bg-gray-700',
      selectedBorderColor: '#4b5563',
      selected: selectedDeadlineTypes.includes('Cutoff'),
      dimmed: selectedDeadlineTypes.length > 0 && !selectedDeadlineTypes.includes('Cutoff'),
      onClick: () => setSelectedDeadlineTypes(prev => toggleArrayValue(prev, 'Cutoff')),
    },
    {
      id: 'appointment',
      label: 'Appointment',
      value: dataForDeadlineStats.filter(m => m.deadline_type === 'Appointment').length,
      colorClass: 'text-body',
      selectedBgClass: 'bg-gray-200 dark:bg-gray-700',
      selectedBorderColor: '#4b5563',
      selected: selectedDeadlineTypes.includes('Appointment'),
      dimmed: selectedDeadlineTypes.length > 0 && !selectedDeadlineTypes.includes('Appointment'),
      onClick: () => setSelectedDeadlineTypes(prev => toggleArrayValue(prev, 'Appointment')),
    },
  ], [dataForDeadlineStats, selectedDeadlineTypes, selectedKeyStatuses]);

  // Column definitions for TanTable
  const baseColumns = useMemo<ColumnDef<AtRiskMove>[]>(() => [
    {
      accessorKey: 'risk_level',
      header: 'Risk',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      sortingFn: (rowA, rowB) => {
        const a = RISK_ORDER[rowA.original.risk_level] ?? 5;
        const b = RISK_ORDER[rowB.original.risk_level] ?? 5;
        return a - b;
      },
      cell: ({ row }) => {
        const riskLevel = row.original.risk_level;
        const riskFactor = row.original.risk_factor;
        let badge: React.ReactNode = null;
        switch (riskLevel) {
          case 'critical':
            badge = (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                Critical
              </span>
            );
            break;
          case 'at_risk':
            badge = (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                <ClockIcon className="w-3 h-3 mr-1" />
                At Risk
              </span>
            );
            break;
          case 'on_track':
            badge = (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                On Track
              </span>
            );
            break;
          case 'arrived':
            badge = (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                Arrived
              </span>
            );
            break;
          case 'unknown':
          default:
            // No deadline could be determined for this leg (no LFD, appointment,
            // or per-diem date), so risk can't be computed. Surface it explicitly
            // instead of rendering a blank cell.
            badge = (
              <Tooltip
                placement="top"
                content="Deadline Not Found — no LFD, appointment, or per-diem date on this leg. Set a deadline date to enable risk tracking."
              >
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700/40 dark:text-gray-300 cursor-help">
                  <QuestionMarkCircleIcon className="w-3 h-3 mr-1" />
                  No Deadline
                </span>
              </Tooltip>
            );
            break;
        }
        let content = badge;
        if (riskFactor === 'recovery_time') {
          content = (
            <Tooltip
              placement="top"
              content="Flagged early to allow recovery time at this destination — replacement driver may be needed."
            >
              <span className="cursor-help">{badge}</span>
            </Tooltip>
          );
        }
        if (row.original._filteredOut) {
          return (
            <div className="flex items-center gap-1.5">
              {content}
              <Tooltip
                placement="top"
                content="Present but hidden by your current status/deadline filters — shown because it matches your search."
              >
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[0.625rem] font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 cursor-help">
                  Filtered
                </span>
              </Tooltip>
            </div>
          );
        }
        return content;
      },
    },
    {
      accessorKey: 'reschedule_count',
      header: 'SF',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => (
        <ServiceFailureBadge
          history={row.original.reschedule_history || []}
          count={row.original.reschedule_count}
          placement="right"
        />
      ),
    },
    {
      accessorKey: 'trouble_ticket_count',
      header: 'TT',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => (
        <TroubleTicketBadge
          tickets={row.original.trouble_tickets || []}
          count={row.original.trouble_ticket_count}
          onClick={() => openOrderDrawer(row.original.pronumber)}
          placement="right"
        />
      ),
    },
    {
      id: 'controllable',
      header: 'C/U',
      accessorFn: (row) => {
        const reason = row.reschedule_history?.[0]?.reason ?? '';
        const idx = reason.indexOf(':');
        if (idx <= 0) return '';
        const prefix = reason.slice(0, idx).trim().toUpperCase();
        return prefix === 'C' || prefix === 'U' ? prefix : '';
      },
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ getValue }) => <span>{getValue<string>()}</span>,
    },
    {
      id: 'subreason',
      header: 'Subreason',
      accessorFn: (row) => {
        const reason = row.reschedule_history?.[0]?.reason ?? '';
        const idx = reason.indexOf(':');
        if (idx < 0) return '';
        return reason.slice(idx + 1).trim();
      },
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ getValue }) => <span>{getValue<string>()}</span>,
    },
    {
      accessorKey: 'pronumber',
      header: 'PRO#',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => {
        const viewed = isViewed(row.original.pronumber);
        return (
          <div
            className={`flex items-center gap-1 px-1 rounded transition-colors duration-150 ${
              viewed ? 'bg-green-200 dark:bg-green-600/50' : ''
            }`}
            title={viewed ? 'Viewed' : undefined}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                markViewed(row.original.pronumber);
                openOrderDrawer(row.original.pronumber);
              }}
              className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
            >
              {row.original.pronumber}-{row.original.legnum}
            </button>
            <a
              href={`https://cloud.gwii.net/cgi-bin/cms/index_cms.pl?action=savecustomsearch&contlist=${row.original.pronumber}&searchtype=pronumber`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                markViewed(row.original.pronumber);
              }}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              title="Open in CMSV1"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </a>
          </div>
        );
      },
    },
    {
      accessorKey: 'driver',
      header: 'Driver',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => {
        const move = row.original;
        if (move.driver && move.status !== 'Not Dispatched') {
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedDriverUnit(move.driver);
              }}
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline font-medium"
            >
              {move.driver}<MuleBadge isMule={move.isMule} />
            </button>
          );
        }
        return <span className="text-gray-400 dark:text-gray-500">-</span>;
      },
    },
    {
      accessorKey: 'movetype',
      header: 'Move Type',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => {
        const movetype = row.original.movetype;
        return (
          <span className={`text-xs font-medium px-2 py-0.5 rounded ${
            movetype === 'IMPORT'
              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
              : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
          }`}>
            {movetype}
          </span>
        );
      },
    },
    {
      accessorKey: 'scac',
      header: 'SCAC',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ getValue }) => {
        const value = getValue<string>();
        return value ? (
          <span className="text-sm text-body">{value}</span>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">-</span>
        );
      },
    },
    {
      accessorKey: 'container',
      header: 'Container',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ getValue }) => {
        const value = getValue<string>();
        return value ? (
          <span className="text-sm text-body">{value}</span>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">-</span>
        );
      },
    },
    {
      accessorKey: 'bol',
      header: 'BOL',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ getValue }) => {
        const value = getValue<string>();
        return value ? (
          <span className="text-sm text-body">{value}</span>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">-</span>
        );
      },
    },
    {
      id: 'requirements',
      header: 'Req',
      size: 100,
      cell: ({ row }) => <RequirementIcons row={row.original} />,
    },
    {
      accessorKey: 'customer_name',
      header: 'Customer',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ getValue }) => {
        const value = getValue<string>();
        return value ? (
          <Tooltip content={value} placement="top">
            <span className="text-sm text-body truncate max-w-[120px] block">
              {value}
            </span>
          </Tooltip>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">-</span>
        );
      },
    },
    {
      accessorKey: 'origin_name',
      header: 'Origin',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      meta: { disableTruncationTooltip: true },
      cell: ({ row }) => {
        const o = row.original;
        const hasHours = o.orig_rcvhours1 || o.orig_rcvhours2;
        const hasContact = o.orig_contact_name && o.orig_contact_name !== 'NONE';
        const tooltipContent = (
          <div className="text-xs space-y-1 min-w-[160px]">
            <div className="font-semibold text-sm">{o.origin_name}</div>
            {hasHours && (
              <div><span className="font-semibold">Rcv Hours:</span> {[o.orig_rcvhours1, o.orig_rcvhours2].filter(Boolean).join(' - ')}</div>
            )}
            {hasContact && (
              <div className="mt-1">
                <div className="font-semibold">{o.orig_contact_name}</div>
                {o.orig_contact_phone && <div>{o.orig_contact_phone}</div>}
                {o.orig_contact_email && <div>{o.orig_contact_email}</div>}
              </div>
            )}
          </div>
        );
        return (
          <Tooltip content={tooltipContent} placement="right">
            <div>
              <div className="text-sm text-body font-medium truncate max-w-[180px]">
                {o.origin_name}
              </div>
              <div className="text-xs text-muted">
                {o.origin_city_state}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'dest_name',
      header: 'Destination',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      meta: { disableTruncationTooltip: true },
      cell: ({ row }) => {
        const o = row.original;
        const hasHours = o.dest_rcvhours1 || o.dest_rcvhours2;
        const hasContact = o.dest_contact_name && o.dest_contact_name !== 'NONE';
        const tooltipContent = (
          <div className="text-xs space-y-1 min-w-[160px]">
            <div className="font-semibold text-sm">{o.dest_name}</div>
            {hasHours && (
              <div><span className="font-semibold">Rcv Hours:</span> {[o.dest_rcvhours1, o.dest_rcvhours2].filter(Boolean).join(' - ')}</div>
            )}
            {hasContact && (
              <div className="mt-1">
                <div className="font-semibold">{o.dest_contact_name}</div>
                {o.dest_contact_phone && <div>{o.dest_contact_phone}</div>}
                {o.dest_contact_email && <div>{o.dest_contact_email}</div>}
              </div>
            )}
          </div>
        );
        return (
          <Tooltip content={tooltipContent} placement="right">
            <div>
              <div className="text-sm text-body font-medium truncate max-w-[180px]">
                {o.dest_name}
              </div>
              <div className="text-xs text-muted">
                {o.dest_city_state}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'appointment_time',
      header: 'Appt',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ getValue }) => {
        const value = getValue<string>();
        return <span className="text-sm text-body">{value || '-'}</span>;
      },
    },
    {
      accessorKey: 'deadline_time',
      header: 'Deadline',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.deadline_sort_timestamp;
        const b = rowB.original.deadline_sort_timestamp;
        if (!a && !b) return 0;
        if (!a) return 1;
        if (!b) return -1;
        return a.localeCompare(b);
      },
      cell: ({ row }) => {
        const value = row.original.deadline_time;
        const isPastDue = row.original.per_diem_past_due;
        return (
          <span
            className={`text-sm font-medium ${isPastDue ? 'text-red-600 dark:text-red-400' : 'text-body'}`}
            style={{ whiteSpace: 'pre-line' }}
          >
            {value}
          </span>
        );
      },
    },
    {
      accessorKey: 'time_remaining_minutes',
      header: 'Time Left',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => {
        const minutes = row.original.time_remaining_minutes;
        return (
          <span className={`text-sm ${
            minutes < 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-body'
          }`}>
            {formatMinutes(minutes)}
          </span>
        );
      },
    },
    {
      accessorKey: 'distance_to_dest_miles',
      header: 'Distance',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.distance_to_dest_miles ?? Infinity;
        const b = rowB.original.distance_to_dest_miles ?? Infinity;
        return a - b;
      },
      cell: ({ row }) => {
        const move = row.original;
        if (move.has_realtime_location) {
          return (
            <span className="inline-flex items-center text-sm text-body">
              <MapPinIcon className="w-3 h-3 mr-1 text-green-500" title="Live GPS" />
              {move.distance_to_dest_miles !== null ? `${move.distance_to_dest_miles} mi` : '-'}
            </span>
          );
        }
        // Only show "No GPS" if there's a driver assigned
        if (move.driver && move.status !== 'Not Dispatched') {
          return <span className="text-xs text-gray-400 dark:text-gray-500">No GPS</span>;
        }
        return null;
      },
    },
    {
      accessorKey: 'estimated_minutes',
      header: 'Est. Travel',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => (
        <span className="text-sm text-body">
          {formatMinutes(row.original.estimated_minutes)}
        </span>
      ),
    },
    {
      id: 'projected_eta',
      header: 'ETA',
      enableSorting: true,
      // Display-only column (no accessor), so the Excel export can't derive a
      // value from the row — give it an explicit exportValue mirroring the cell.
      meta: {
        exportValue: (row: any) => {
          if (row.status === 'Completed' || row.status === 'At Destination' || row.status === 'Not Dispatched') {
            return '-';
          }
          return driverETAs[`${row.pronumber}-${row.legnum}`] || '-';
        },
      },
      sortingFn: (rowA, rowB) => {
        const etaA = driverETAs[`${rowA.original.pronumber}-${rowA.original.legnum}`] || '';
        const etaB = driverETAs[`${rowB.original.pronumber}-${rowB.original.legnum}`] || '';
        return etaA.localeCompare(etaB);
      },
      cell: ({ row }) => {
        const move = row.original;
        if (move.status === 'Completed' || move.status === 'At Destination' || move.status === 'Not Dispatched') {
          return <span className="text-xs text-gray-400">-</span>;
        }
        const eta = driverETAs[`${move.pronumber}-${move.legnum}`];
        if (!eta) return <span className="text-xs text-gray-400">-</span>;
        return (
          <span className="text-sm font-medium text-body">
            {eta}
          </span>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ getValue }) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">{getValue<string>()}</span>
      ),
    },
    {
      accessorKey: 'order_lc',
      header: 'Order LC',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ getValue }) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">{getValue<string>() || '-'}</span>
      ),
    },
    {
      accessorKey: 'dispatcher',
      header: 'Dispatcher',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ getValue }) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">{getValue<string>()}</span>
      ),
    },
    {
      accessorKey: 'dropreleasedatetime',
      header: 'DR Date',
      enableSorting: true,
      cell: ({ getValue }) => {
        const value = getValue<string | null>();
        if (!value) return <span className="text-gray-400 dark:text-gray-500">-</span>;
        const date = new Date(value);
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return <span className="text-sm text-label">{month}/{day} {hours}:{minutes}</span>;
      },
    },
    {
      accessorKey: 'dropreleasecomment',
      header: 'DR Comment',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ getValue }) => {
        const value = getValue<string>();
        return value ? (
          <Tooltip content={value} placement="left">
            <span className="text-sm text-gray-600 dark:text-gray-400 truncate max-w-[150px] block cursor-help">
              {value}
            </span>
          </Tooltip>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">-</span>
        );
      },
    },
    {
      id: 'map',
      header: 'Map',
      enableSorting: false,
      cell: ({ row }) => {
        const move = row.original;
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMapDrawerMove(move);
            }}
            disabled={!move.has_realtime_location}
            className={`p-1.5 rounded-md transition-colors ${
              move.has_realtime_location
                ? 'text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/30'
                : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
            }`}
            title={move.has_realtime_location ? 'View on map' : 'No GPS location available'}
          >
            <MapIcon className="h-5 w-5" />
          </button>
        );
      },
    },
  ], [formatMinutes, openOrderDrawer, driverETAs, isViewed, markViewed]);

  // No isSubRow: every row here is a move in its own right, so an added order column
  // repeats across a pro's legs rather than blanking.
  const { columns, availableFields } = useAdditionalFieldColumns<AtRiskMove>(
    fieldSelection,
    { baseColumns, coveredFields: AT_RISK_COVERED_FIELDS },
  );

  // Row class for risk highlighting and map drawer selection
  const getRowClassName = useCallback((row: AtRiskMove) => {
    // Highlight if this row is selected for the map drawer
    const isMapSelected = mapDrawerMove &&
      mapDrawerMove.pronumber === row.pronumber &&
      mapDrawerMove.legnum === row.legnum;

    if (isMapSelected) {
      return 'bg-purple-100 dark:bg-purple-900/30 ring-2 ring-purple-500 ring-inset';
    }
    // Rows surfaced by search but hidden by the active status/deadline buttons.
    if (row._filteredOut) {
      return 'border-l-4 border-orange-400 bg-orange-50/60 dark:bg-orange-900/15';
    }
    if (row.risk_level === 'critical') return 'bg-red-50/50 dark:bg-red-900/10';
    if (row.risk_level === 'at_risk') return 'bg-yellow-50/50 dark:bg-yellow-900/10';
    if (row.risk_level === 'arrived') return 'bg-blue-50/50 dark:bg-blue-900/10';
    return '';
  }, [mapDrawerMove]);

  // Indicator cell color and label based on new location or key account status
  const getIndicatorInfo = useCallback((row: AtRiskMove): { color: string; label: string | null } | null => {
    const isNewLocation = row.dest_is_new_location;

    // New location takes priority - blue
    if (isNewLocation === 'Y') {
      return { color: 'bg-blue-500', label: 'New Location' };
    }
    // Otherwise use key account status
    switch (row.key_account?.toUpperCase()) {
      case 'KEY ACCOUNTS 1':
        return { color: 'bg-red-500', label: 'Key Account 1' };
      case 'KEY ACCOUNTS 2':
        return { color: 'bg-orange-500', label: 'Key Account 2' };
      case 'KEY ACCOUNTS 3':
        return { color: 'bg-yellow-400', label: 'Key Account 3' };
      case 'KEY ACCOUNTS 4':
        return { color: 'bg-green-500', label: 'Key Account 4' };
      case 'CONTRACT':
        return { color: 'bg-lime-500', label: 'Contract' };
      case 'KEY PROSPECT 1':
        return { color: 'bg-purple-500', label: 'Key Prospect 1' };
      case 'KEY PROSPECT 2':
        return { color: 'bg-purple-500', label: 'Key Prospect 2' };
      case 'KEY PROSPECT 3':
        return { color: 'bg-purple-500', label: 'Key Prospect 3' };
      case 'KEY PROSPECT 4':
        return { color: 'bg-purple-500', label: 'Key Prospect 4' };
      case 'HYPER FOCUS':
        return { color: 'bg-blue-500', label: 'Hyper Focus' };
      case 'AVERAGE':
      default:
        // No color - return null to use default background
        return null;
    }
  }, []);

  return (
    <div className={`${styles.pageContainer} ${showGridBorders ? styles.pageContainerBorders : ''} ${showGridHighlight ? styles.pageContainerHighlight : ''} ${showGridLabels ? styles.pageContainerLabels : ''}`}>
      {/* Loading Overlay - shown during initial home region setup */}
      {initializing && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Loading...</span>
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <FilterBar label="Filters:" className={styles.filterBar}>
        <GridLabel areaName="filterBar" position="top-right" />
        {/* Today / Tomorrow. Styled to sit beside the sm MultiSelect triggers. */}
        <div className="relative inline-block">
          <select
            aria-label="Day"
            title="Today is the live board. Tomorrow shows only moves whose appointment or deadline falls tomorrow."
            value={dayScope}
            onChange={(e) => setDayScope(e.target.value as AtRiskDayScope)}
            className={`appearance-none py-1 ps-2 pe-6 text-xs font-medium cursor-pointer rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white dark:focus:ring-gray-600 ${
              dayScope === 'tomorrow'
                ? 'bg-amber-50 border-amber-400 text-amber-900 dark:bg-amber-900/40 dark:border-amber-500 dark:text-amber-100'
                : 'bg-white border-gray-400 dark:bg-gray-900 dark:border-gray-500'
            }`}
          >
            <option value="today">Today</option>
            <option value="tomorrow">Tomorrow</option>
          </select>
          <div className="absolute top-1/2 end-1 -translate-y-1/2 pointer-events-none">
            <ChevronUpDownIcon className="w-3 h-3 text-gray-500 dark:text-gray-500" />
          </div>
        </div>
        <OrderDispatcherFilter
          value={selectedOrderDispatchers}
          onChange={setSelectedOrderDispatchers}
        />
        <DriverDispatcherFilter
          value={selectedDriverDispatchers}
          onChange={setSelectedDriverDispatchers}
        />
        <MultiSelect
          options={regionOptions}
          value={selectedRegions}
          onChange={setSelectedRegions}
          placeholder="All Regions"
          showClearOption={true}
          clearOptionLabel="All Regions"
          enableSearch={true}
          searchPlaceholder="Search regions..."
          size="sm"
          className="min-w-[130px]"
        />
        <MultiSelect
          options={csrOptions}
          value={selectedCSRs}
          onChange={setSelectedCSRs}
          placeholder="All Order LCs"
          showClearOption={true}
          clearOptionLabel="All Order LCs"
          enableSearch={true}
          searchPlaceholder="Search LCs..."
          size="sm"
          className="min-w-[140px]"
          preserveOrder={true}
        />
        <MultiSelect
          options={salesRepOptions}
          value={selectedSalesReps}
          onChange={setSelectedSalesReps}
          placeholder="All Sales Reps"
          showClearOption={true}
          clearOptionLabel="All Sales Reps"
          enableSearch={true}
          searchPlaceholder="Search sales reps..."
          size="sm"
          className="min-w-[140px]"
        />
        <MultiSelect
          options={customerOptionsWithCounts}
          value={selectedCustomers}
          onChange={setSelectedCustomers}
          placeholder="All Customers"
          showClearOption={true}
          clearOptionLabel="All Customers"
          enableSearch={true}
          searchPlaceholder="Search customers..."
          size="sm"
          className="min-w-[150px]"
          preserveOrder={true}
        />
        <MultiSelect
          options={keyStatusOptions}
          value={selectedKeyStatuses}
          onChange={setSelectedKeyStatuses}
          placeholder="All Key Statuses"
          showClearOption={true}
          clearOptionLabel="All Key Statuses"
          size="sm"
          className="min-w-[150px]"
        />
        <button
          type="button"
          role="switch"
          aria-checked={showMules}
          onClick={() => setShowMules((current) => !current)}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <span aria-hidden="true" className={`inline-flex h-4 w-7 shrink-0 items-center rounded-full p-0.5 transition-colors ${showMules ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
            <span className={`h-3 w-3 rounded-full bg-white shadow-sm transition-transform ${showMules ? 'translate-x-3' : ''}`} />
          </span>
          Show mules
        </button>
        {(selectedRiskLevels.length > 0 || selectedStatuses.length > 0 || selectedDeadlineTypes.length > 0 ||
          selectedOrderDispatchers.length > 0 || selectedDriverDispatchers.length > 0 || selectedRegions.length > 0 ||
          selectedCSRs.length > 0 || selectedSalesReps.length > 0 || selectedCustomers.length > 0 ||
          selectedKeyStatuses.length > 0 || dayScope !== 'today' || !showMules) && (
          <button
            onClick={() => {
              // Back to the live board (not persisted, so nothing to save)
              setDayScope('today');
              setShowMules(true);
              // Clear stat box filters (auto-saved by useEffect)
              setSelectedRiskLevels([]);
              setSelectedStatuses([]);
              setSelectedDeadlineTypes([]);
              // Clear dropdown filters (global)
              setSelectedOrderDispatchers([]);
              setSelectedDriverDispatchers([]);
              setSelectedRegions([]);
              // Clear local filters (auto-saved by useEffect)
              setSelectedCSRs([]);
              setSelectedSalesReps([]);
              setSelectedCustomers([]);
              setSelectedKeyStatuses([]);
              // Reset home region initialization so it can re-apply if needed
              homeRegionInitialized.current = false;
            }}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
          >
            Clear Filters
          </button>
        )}
      </FilterBar>

      {/* Stat Bar */}
      <div className={`${styles.statBar} px-6 py-3 bg-white dark:bg-gray-900 space-y-1`} style={{ position: 'relative' }}>
        <GridLabel areaName="statBar" position="top-right" />
        {/* Lives inside the statBar grid area: the page grid has exactly three
            rows, so a sibling here would be auto-placed and break the layout. */}
        {dayScope === 'tomorrow' && (
          <div
            role="status"
            className="mb-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-3 py-1.5 text-sm text-amber-900 dark:text-amber-100"
          >
            <span className="font-semibold">
              Viewing Tomorrow{formatScopeDate(scopeDate) ? `, ${formatScopeDate(scopeDate)}` : ''}.
            </span>{' '}
            Only moves whose appointment or deadline falls tomorrow. Past-due and missed appointments stay on Today.
          </div>
        )}
        <StatsGrid
          stats={riskLevelStats}
          columns={3}
          rows={1}
          size="lg"
          loading={loading && baseData.length === 0}
        />
        <StatsGrid
          stats={statusStats}
          columns={5}
          rows={1}
          size="md"
          loading={loading && baseData.length === 0}
          bgColor="bg-gray-100 dark:bg-gray-800"
        />
        <StatsGrid
          stats={deadlineTypeStats}
          columns={5}
          rows={1}
          size="sm"
          loading={loading && baseData.length === 0}
          bgColor="bg-white dark:bg-gray-900"
        />
      </div>

      {/* Body */}
      <div className={`${styles.body} p-6 flex flex-col gap-4 overflow-hidden`} style={{ position: 'relative' }}>
        <GridLabel areaName="body" position="top-right" />
        {/* Error Alert */}
        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* TanTable */}
        <TanTable
          data={tableData}
          onSearchChange={setTableSearch}
          columns={columns}
          availableFields={availableFields}
          additionalFields={fieldSelection.additionalFields}
          onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
          loading={loading}
          settingsKey="at-risk-monitor"
          cardTitle="At Risk Monitor"
          searchPlaceholder="Search moves..."
          enableColumnFilters={true}
          columnFilterTypes={{ customer_name: 'text' }}
          enableSearch={true}
          enableExcelExport={true}
          defaultPageSize={100}
          defaultSorting={[{ id: 'risk_level', desc: false }, { id: 'time_remaining_minutes', desc: false }]}
          getRowClassName={getRowClassName}
          fillContainer={true}
          showIndicator={true}
          getIndicatorInfo={getIndicatorInfo}
        />

        {/* Map Drawer */}
        <AtRiskMapDrawer
          open={mapDrawerMove !== null}
          onClose={() => setMapDrawerMove(null)}
          move={mapDrawerMove}
        />

        {/* Driver Loads Drawer */}
        <DriverLoadsDrawerV2
          isOpen={selectedDriverUnit !== null}
          onClose={() => setSelectedDriverUnit(null)}
          unitNumber={selectedDriverUnit}
        />

        {/* Order Details Drawer */}
        <ProDetailSlideOver
          pronumber={selectedPro}
          isOpen={isOrderDrawerOpen && selectedPro !== null}
          onClose={closeOrderDrawer}
        />
      </div>
    </div>
  );
}

function AtRiskMonitor() {
  return (
    <ViewedProsProvider>
      <AtRiskMonitorContent />
    </ViewedProsProvider>
  );
}

export default AtRiskMonitor;
